'use client'

import { openLogin } from '@/components/auth/open-auth'
import { HomeContainer } from '@/components/common'
import { Button } from '@/components/ui/button'
import { useIsLoggedIn } from '@/hooks/react-query/user/use-user'
import { ImageHelper } from '@/lib/helpers/image.helper'

import { sanitize } from '@/lib/helpers/sanitize'
import { cn, formatNumber } from '@/lib/utils'
import { ClubMemberActivity } from '@/types/club'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'

const config = {
  title: 'awareness.title',
  memberText: 'awareness.memberText',
  items: [
    {
      title: 'awareness.items.0.title',
      link: '/coolclub#referral-friend-section?itm_source=awareness',
      description: 'awareness.items.0.description',
      icon: {
        url: '/image/March2025/mceclip4_90.png',
        width: 18,
        height: 26,
      },
    },
    {
      title: 'awareness.items.1.title',
      // description: '(X2 vào thứ 6)',
      link: '',
      icon: {
        url: '/image/November2025/mceclip0_63.png',
        width: 32,
        height: 26,
      },
    },
    {
      title: 'awareness.items.2.title',
      description: 'awareness.items.2.description',
      link: '',
      icon: {
        url: '/image/March2025/mceclip2_83.png',
        width: 22,
        height: 26,
      },
    },
  ],
  recentText: 'awareness.recentText',
  linkClub: '/coolclub?itm_source=awareness',
}

type Props = {
  data: ClubMemberActivity[]
  className?: string
  total: number
}

export const CoolClubAwarenessContent: React.FC<Props> = ({
  data,
  className,
  total,
}) => {
  const isLoggedIn = useIsLoggedIn()
  const t = useTranslations('button')
  const tAwareness = useTranslations()
  const splitArray = (arr: ClubMemberActivity[], idx: number) => {
    const chunkSize = Math.ceil(arr.length / idx)
    const start = (idx - 1) * chunkSize
    return arr.slice(start, start + chunkSize)
  }

  return (
    <HomeContainer>
      <div
        className={cn(
          'rounded-2xl lg:mb-15 lg:flex lg:pt-11',
          'bg-neutral-100',
          className
        )}
      >
        <div className='p-3 lg:flex-1 lg:border-0 lg:px-15'>
          <h4 className='mb-2 text-center font-criteria text-base font-bold uppercase text-neutral-700 lg:text-2xl'>
            {tAwareness(config.title)}
            <br />{' '}
            <span className='text-primary-600'>
              {' '}
              {formatNumber(total)}
            </span>{' '}
            {tAwareness(config.memberText)}
          </h4>

          <ul className='flex items-stretch gap-1 lg:space-x-4 lg:space-y-0'>
            {config.items.map((item) => (
              <li
                key={tAwareness(item.title)}
                className='relative flex-1 overflow-hidden rounded-xl bg-primary-500 px-3 py-2 lg:h-32'
                // className='relative overflow-hidden rounded-lg bg-primary-500 px-4 py-2 lg:h-32 lg:w-44 lg:px-5 lg:py-5 2xl:w-64'
              >
                <Link
                  href={item.link}
                  className='flex h-full flex-col items-start justify-between gap-2 text-light'
                  ga-tracking-value={`coolclub-awareness__privilege__${tAwareness(item.title).toLowerCase().replace(/\s+/g, '-')}`}
                  ga-tracking-label={tAwareness(item.title)}
                  prefetch={false}
                >
                  <p className='font-sans text-sm font-semibold leading-4.5 text-light lg:text-xl lg:leading-6'>
                    {tAwareness(item.title)}{' '}
                    {item.description && tAwareness(item.description)}
                  </p>
                  <Image
                    src={ImageHelper.getImageUrl(item.icon.url)}
                    alt={
                      tAwareness(item.title) +
                      (item.description
                        ? ` ${tAwareness(item.description)}`
                        : '')
                    }
                    width={item.icon.width}
                    height={item.icon.height}
                    loading='lazy'
                    decoding='async'
                    className='max-w-8 lg:absolute lg:bottom-5 lg:right-5'
                  />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div
        className={cn(
          'relative mb-10 mt-2 overflow-hidden rounded-2xl p-3 before:absolute before:left-0 before:top-0 before:h-full before:w-[3px] before:bg-gradient-to-b before:from-transparent before:via-neutral-900/20 before:to-transparent max-lg:before:hidden lg:basis-5/12 lg:px-15',
          'bg-neutral-100'
        )}
      >
        <p className='mb-3 text-center font-criteria text-base font-bold uppercase leading-6 text-neutral-800 lg:font-medium'>
          {tAwareness(config.recentText)}
        </p>

        <div
          className='relative overflow-hidden before:absolute before:-left-9 before:top-0 before:z-[1] before:h-full before:w-1/5 before:rotate-180 before:bg-[linear-gradient(270deg,_#F3EFFF_28.79%,_rgba(243,239,255,0)_100%)] after:absolute after:-right-9 after:top-0 after:z-[1] after:h-full after:w-1/5 after:rotate-180 after:bg-[linear-gradient(270deg,_rgba(243,239,255,0)_0%,_#F3EFFF_51.21%)] lg:before:hidden lg:after:hidden'
          role='region'
          aria-label='Recent activities'
        >
          {Array.from({ length: 2 }).map((_, idx) => {
            const activities = splitArray(data, idx + 1)

            return (
              <ul
                className={`flex gap-3 whitespace-nowrap ${idx === 1 ? 'animate-marquee2' : 'animate-marquee'}`}
                key={idx}
                role='list'
              >
                {activities.map((activity, idxActivity) => (
                  <li
                    className='font-sans text-base text-neutral-600'
                    key={idxActivity}
                    dangerouslySetInnerHTML={{
                      __html: sanitize(activity.content),
                    }}
                    role='listitem'
                    aria-label={sanitize(activity.content)}
                  ></li>
                ))}
              </ul>
            )
          })}
        </div>

        {isLoggedIn ? (
          <Button
            className='mt-4 w-full uppercase lg:mt-6 lg:h-10'
            variant={'black'}
            ga-tracking-value='coolclub-awareness__explore-coolclub'
            ga-tracking-label={t('explore_coolclub')}
            asChild
          >
            <Link href={config.linkClub} className='uppercase'>
              {t('explore_coolclub')}
              <Image
                src='/icons/icon-arrow-line-right.svg'
                width={15}
                height={16}
                alt='icon arrow right'
                loading='lazy'
                decoding='async'
                className='transition-transform duration-200 group-hover:translate-x-1'
              />
            </Link>
          </Button>
        ) : (
          <Button
            className='mt-4 w-full uppercase lg:mt-6 lg:h-10'
            variant={'black'}
            ga-tracking-value='coolclub-awareness__join-coolclub'
            ga-tracking-label={t('join_coolclub')}
            onClick={() => {
              openLogin()
            }}
          >
            {t('join_coolclub')}
            <Image
              src='/icons/icon-arrow-line-right.svg'
              width={15}
              height={16}
              alt='icon arrow right'
              loading='lazy'
              decoding='async'
              className='transition-transform duration-200 group-hover:translate-x-1'
            />
          </Button>
        )}
      </div>
    </HomeContainer>
  )
}
